import React, { type ReactNode } from 'react';

import PropTypes from 'prop-types';

import { IconButton } from '@adsk/alloy-react-button';
import {
  Container,
  hasTailwindBackgroundColor,
  hasTailwindTextColor,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import { XIcon } from '@adsk/alloy-react-icon';
import { useTheme, CSSBlock } from '@adsk/alloy-react-styles';
import { StylableComponent, stylePropType } from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { getTokenAriaLabel } from '../../helpers';
import { useTranslation } from '../../i18n';

export type TokenProps = Omit<StylableComponent<HTMLDivElement>, 'content'> & {
  content?: ReactNode;
  disabled?: boolean;
  container?: Container;
  onClickX?: () => void;
  renderLeftComponent?: () => ReactNode;
  onRemove?: () => void;
};

const REMOVAL_KEYS = ['Delete', 'Backspace'];

const Token = React.forwardRef<HTMLDivElement, TokenProps>(
  (
    {
      className,
      style,
      disabled,
      content,
      container,
      onClickX,
      renderLeftComponent,
      onRemove,
      ...props
    },
    ref,
  ) => {
    const tokenRef = useForwardedRef(ref);
    const { t } = useTranslation();
    const { tokens } = useTheme();
    const tokenStyles = tokens?.component.dropdown?.control?.token;

    const consumerSetsBackground = hasTailwindBackgroundColor(className);
    const consumerSetsColor = hasTailwindTextColor(className);

    const _ariaLabel = props['aria-label'];

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }

        if (REMOVAL_KEYS.includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          onRemove?.();
        }
      },
      [disabled, onRemove],
    );

    const handleXClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onClickX?.();
    };

    const handleXKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      // Enter and Space will already have been handled as part of onClick
      if (REMOVAL_KEYS.includes(e.key)) {
        onClickX?.();
      }
    };

    const interactiveTokenProps = React.useMemo(() => {
      if (disabled) return {};

      const [type, label] = getTokenAriaLabel({
        content,
        'aria-label': _ariaLabel,
      });

      const ariaLabel = type === 'OWN' ? label : t(type, { label });

      return {
        onKeyDown: handleKeyDown,
        tabIndex: 0,
        role: 'button',
        'aria-label': ariaLabel,
      };
    }, [disabled, handleKeyDown, content, _ariaLabel, t]);

    const tokenCss = React.useMemo(() => {
      const { background, color, ...rest } = tokenStyles?._ ?? {};

      const base: CSSBlock = {
        display: 'flex',
        height: '24px',
        'align-items': 'center',
        cursor: disabled ? 'not-allowed' : 'inherit',
        'user-select': 'none',
        'min-width': 0,
      };

      return [
        base,
        rest,
        consumerSetsBackground ? undefined : { background },
        consumerSetsColor
          ? undefined
          : { color: disabled ? tokenStyles?.disabled?._?.color : color },
        style,
      ];
    }, [
      tokenStyles,
      disabled,
      consumerSetsBackground,
      consumerSetsColor,
      style,
    ]);

    return (
      <div
        ref={tokenRef}
        className={className}
        data-testid={'token'}
        {...interactiveTokenProps}
        css={tokenCss}
        {...props}
      >
        {renderLeftComponent?.()}
        <OverflowTooltip
          singleLine
          autoTipOnEllipsis
          target={tokenRef}
          container={container}
          getTargetProps={() => ({ style: { maxWidth: 172, minWidth: 0 } })}
        >
          {content}
        </OverflowTooltip>
        <IconButton
          renderIcon={XIcon.renderIcon}
          size={16}
          style={{
            marginLeft: tokenStyles?.icon?._?.['margin-left'],
          }}
          disabled={disabled}
          onClick={handleXClick}
          onKeyDown={handleXKeyDown}
        />
      </div>
    );
  },
);

Token.displayName = 'Token';

Token.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Token content */
  content: PropTypes.any,
  /** If the token is unclickable */
  disabled: PropTypes.bool,
  /** The container */
  container: PropTypes.any,
  /** Function executed upon clicking the X icon */
  onClickX: PropTypes.func,
  /** component to render to the left of the token's content (outside the overflow tooltip) */
  renderLeftComponent: PropTypes.func,
  /** Function executed when Backspace or Delete key is pressed while token is focused */
  onRemove: PropTypes.func,
};

export default Token;
